// EU AI Act Art. 50 transparency labelling for AI-generated artefacts
// (PRD §8: "KI-generierte Ausgaben als KI gekennzeichnet — Metadaten/sichtbarer
// Hinweis"). Three surfaces:
//   1. aiLabelMetadata()      — the canonical machine-readable label (mirrors the
//                               edge function's AI_LABEL).
//   2. drawAiBadge()          — stamps a visible "KI-generiert" badge onto a
//                               generated bitmap before export/share.
//   3. createAiBadgeElement() — an accessible DOM badge to overlay AI results.
// Pure / DOM-injected; unit-tested.

import { stampCornerText, type StampCtx } from './canvas-stamp';

export interface AiLabel {
  aiGenerated: true;
  generator: string;
  notice: string;
}

// Mirrors supabase/functions/ai-generate AI_LABEL — keep the wording in sync.
export const AI_LABEL: AiLabel = {
  aiGenerated: true,
  generator: 'JOE FLOW AI',
  notice: 'Dieses Bild wurde mit KI erzeugt / This image was generated with AI',
};

export function aiLabelMetadata(): AiLabel {
  return { ...AI_LABEL };
}

export interface AiBadgeOptions {
  text?: string;
  fontScale?: number;
  opacity?: number;
  padding?: number;
}

const BADGE_DEFAULTS = {
  text: '🤖 KI-generiert',
  fontScale: 0.035,
  opacity: 0.85,
  padding: 16,
} as const;

/**
 * Stamp a visible top-left "KI-generiert" badge onto a generated image. Distinct
 * from the brand watermark (different corner/intent: transparency, not branding)
 * and applied to ALL AI output regardless of tier. Returns the font px used.
 */
export function drawAiBadge(
  ctx: StampCtx,
  width: number,
  height: number,
  opts: AiBadgeOptions = {},
): number {
  return stampCornerText(ctx, width, height, {
    text: opts.text ?? BADGE_DEFAULTS.text,
    corner: 'tl',
    fontScale: opts.fontScale ?? BADGE_DEFAULTS.fontScale,
    opacity: opts.opacity ?? BADGE_DEFAULTS.opacity,
    padding: opts.padding ?? BADGE_DEFAULTS.padding,
    minFont: 11,
    maxFont: 48,
    fillStyle: 'rgba(255,255,255,0.95)',
  });
}

/** Accessible DOM badge to overlay on an AI-generated result in the UI. */
export function createAiBadgeElement(doc: Document = document): HTMLElement {
  const el = doc.createElement('span');
  el.className = 'jf-ai-badge';
  el.setAttribute('role', 'img');
  el.setAttribute('aria-label', AI_LABEL.notice);
  el.title = AI_LABEL.notice;
  el.textContent = '🤖 KI-generiert';
  el.style.cssText =
    'display:inline-flex;align-items:center;gap:4px;padding:4px 8px;border-radius:8px;' +
    'background:rgba(15,23,42,.82);color:#f1f5f9;font:600 12px system-ui,sans-serif;';
  return el;
}
